import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import Toast from 'react-native-toast-message';
import * as Crypto from 'expo-crypto';
import Chip from '@/components/General/Chip';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import { Mutation } from '@/utils/react-query/Mutations';
import { extractFollowUpCode } from '@/data/followUp';
import { parseTimeFrame } from '@/utils/dater';
import { formatMealDate, getMealSlotMeta, getMealSlotState } from '@/utils/followUpCare';

/**
 * MealRoster — the meal-date coordination surface on a care episode
 * (spec §13.4–13.5). Every slot renders its NEED (the date) and its
 * STAFFING + DELIVERY state as separate facts: claimed is never shown as
 * delivered (C10), cancelled dates stay visible as history.
 *
 * Team members see a Claim button on open slots — never optimistic: the
 * row only flips when the server accepts (a lost race refetches to the
 * staffed state, C06/C07). Manage / primary caregiver assign anyone in the
 * org through the people picker (follow_up_assign_task replaces a losing
 * claim with an explicit audit). Non-team helpers never reach this
 * surface — they only ever see their own assigned Task (§8.4).
 */
const MealRoster = ({ organizationID, personID, careEpisodeID, mealSlots = [], isActive, canAdminister, loggedInPersonID, onEditDates }) => {
    const router = useRouter();
    const [assigningSlot, setAssigningSlot] = useState(null);
    const [claimingSlotID, setClaimingSlotID] = useState(null);

    const claimMutation = Mutation.followUp.claimMeal({
        onSuccess: (data) => {
            setClaimingSlotID(null);
            Toast.show({
                type: 'success',
                text2: data?.alreadyClaimed ? 'You already claimed this meal' : 'Meal claimed. It still needs to be delivered',
            });
        },
        onError: (error) => {
            setClaimingSlotID(null);
            const code = error?.followUpCode ?? extractFollowUpCode(error);
            if (code === 'FOLLOW_UP_CONFLICT') {
                Toast.show({ type: 'info', text2: 'Someone else just claimed that meal. The roster is up to date now.' });
            } else if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to claim the meal. Please try again.' });
            }
        },
    });

    const assignMutation = Mutation.followUp.assignTask({
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Meal assigned' });
            setAssigningSlot(null);
        },
        onError: (error) => {
            const code = error?.followUpCode ?? extractFollowUpCode(error);
            if (code === 'FOLLOW_UP_CONFLICT') {
                Toast.show({ type: 'info', text2: 'The assignment changed just now. The roster is up to date.' });
            } else if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to assign the meal.' });
            }
        },
    });

    const handleClaim = (slot) => {
        if (claimMutation.isPending || !slot?.mealSlotID) return;
        setClaimingSlotID(slot.mealSlotID);
        claimMutation.mutate({
            organizationID,
            careEpisodeID,
            personID,
            loggedInPersonID,
            mealSlotID: slot.mealSlotID,
            idempotencyKey: Crypto.randomUUID(),
        });
    };

    const slots = [...(mealSlots || [])].sort((a, b) => {
        const aClosed = getMealSlotState(a) === 'CANCELLED' ? 1 : 0;
        const bClosed = getMealSlotState(b) === 'CANCELLED' ? 1 : 0;
        if (aClosed !== bClosed) return aClosed - bClosed;
        return `${a.mealDate}`.localeCompare(`${b.mealDate}`);
    });

    const renderSlot = (slot) => {
        const state = getMealSlotState(slot);
        const meta = getMealSlotMeta(slot);
        const isCancelled = state === 'CANCELLED';
        const claimable = state === 'OPEN' && isActive;
        const canClaimThis = claimable && slot.assigneePersonID !== loggedInPersonID;
        const menuOptions = [];

        // Assign/reassign — manage or primary caregiver. Claim and direct
        // assignment serialize server-side; the loser gets an explicit
        // conflict and the roster refreshes (C07).
        if (canAdminister && !isCancelled && state !== 'DELIVERED' && isActive) {
            menuOptions.push({
                name: slot.assigneePersonID ? 'Reassign…' : 'Assign to…',
                icon: dropdownIcons?.edit ?? 'person-add-outline',
                onSelect: () => setAssigningSlot(slot),
            });
        }

        return (
            <View key={slot.mealSlotID} testID={`meal-slot-${slot.mealSlotID}`} style={[styles.row, isCancelled && styles.rowCancelled]}>
                <View style={styles.iconWrap}>
                    <Unicon name="restaurant-outline" size={16} colorVariant="paths" />
                </View>
                <View style={styles.rowBody}>
                    <Pressable
                        accessibilityRole="button"
                        onPress={() => slot.taskInstanceID && router.push(`/tasks/instances/${slot.taskInstanceID}`)}
                    >
                        <Text style={[styles.dateText, isCancelled && styles.struck]} numberOfLines={1}>
                            {formatMealDate(slot.mealDate)}
                        </Text>
                    </Pressable>
                    <Text style={styles.meta} numberOfLines={2}>
                        {[
                            slot.assigneeName || (state === 'OPEN' ? 'No volunteer yet' : null),
                            slot.deliveryWindow || null,
                            state === 'DELIVERED' && slot.deliveredAt ? `Delivered ${parseTimeFrame(slot.deliveredAt, true)}` : null,
                            isCancelled ? 'Date no longer needed' : null,
                        ].filter(Boolean).join(' · ')}
                    </Text>
                    {!!slot.deliveryInstructions && !isCancelled && (
                        <Text style={styles.instructions} numberOfLines={2}>{slot.deliveryInstructions}</Text>
                    )}
                </View>
                <View style={styles.rowEnd}>
                    <Chip
                        icon={meta.icon}
                        colorVariant={meta.colorVariant}
                        testID={`meal-slot-state-${slot.mealSlotID}`}
                    >
                        {meta.label}
                    </Chip>
                    {canClaimThis && (
                        <SolidButton
                            testID={`meal-claim-${slot.mealSlotID}`}
                            size="sm"
                            variant="outlined"
                            type="primary"
                            iconName="hand-left-outline"
                            text="Claim"
                            disabled={claimMutation.isPending}
                            processing={claimMutation.isPending && claimingSlotID === slot.mealSlotID}
                            onPress={() => handleClaim(slot)}
                        />
                    )}
                    {menuOptions.length > 0 && (
                        <DropdownMenu testID={`meal-slot-menu-${slot.mealSlotID}`} options={menuOptions} />
                    )}
                </View>
            </View>
        );
    };

    return (
        <View style={styles.section} testID="meal-roster">
            <View style={styles.headerRow}>
                <Text style={styles.sectionTitle}>Meals</Text>
                {canAdminister && isActive && (
                    <SolidButton
                        testID="meal-dates-edit"
                        size="sm"
                        variant="outlined"
                        type="secondary"
                        iconName={slots.length ? 'create-outline' : 'add'}
                        text={slots.length ? 'Edit Meal Dates' : 'Set Up Meals'}
                        onPress={onEditDates}
                    />
                )}
            </View>
            {slots.length === 0 ? (
                <Text style={styles.emptyText}>
                    {canAdminister && isActive
                        ? 'No meal dates yet. Set up meals when they would help.'
                        : 'No meals are scheduled.'}
                </Text>
            ) : (
                <View style={styles.card}>{slots.map(renderSlot)}</View>
            )}

            {/* Assign picker — any org member via follow_up_assign_task. */}
            <SheetSelector
                type="people"
                title={assigningSlot ? `Assign the ${formatMealDate(assigningSlot.mealDate)} meal to` : 'Assign to'}
                open={!!assigningSlot}
                onOpen={() => {}}
                onClose={() => setAssigningSlot(null)}
                value={null}
                excludeIds={personID ? [personID] : []}
                onChange={(selected) => {
                    if (!selected?.id || !assigningSlot?.taskInstanceID) return;
                    assignMutation.mutate({
                        organizationID,
                        taskInstanceID: assigningSlot.taskInstanceID,
                        assigneePersonID: selected.id,
                        personID,
                        careEpisodeID,
                    });
                }}
            >
                <View />
            </SheetSelector>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    section: { gap: 6 },
    headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
    sectionTitle: { fontSize: 13, fontFamily: fonts.heading, color: colors.textSecondary },
    emptyText: { fontSize: 13, color: colors.textSecondary, fontStyle: 'italic', paddingHorizontal: 4 },
    card: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        boxShadow: shadows.card,
        overflow: 'hidden',
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: 12,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    rowCancelled: { opacity: 0.6 },
    iconWrap: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    rowBody: { flex: 1, minWidth: 0, gap: 2 },
    dateText: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    struck: { textDecorationLine: 'line-through' },
    meta: { fontSize: 12, color: colors.textSecondary },
    instructions: { fontSize: 12, color: colors.textSecondary, fontStyle: 'italic' },
    rowEnd: { alignItems: 'flex-end', gap: 6 },
}));

export default MealRoster;
